import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { Auction, AuctionLot } from '../types/auction.types';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface UploadedImage {
  file: File;
  sku: string;
  imageNumber: string;
  url?: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

const BulkImageUpload: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const { userProfile, isAuctioneer, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [auction, setAuction] = useState<Auction | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    if (!isAuctioneer && !isAdmin) {
      toast.error('Access denied: Auctioneers only');
      navigate('/');
      return;
    }
    loadAuction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionId]);

  const loadAuction = async () => {
    if (!auctionId) return;

    try {
      const auctionDoc = await getDoc(doc(db, 'auctions', auctionId));
      
      if (auctionDoc.exists()) {
        const data = auctionDoc.data();
        const auctionData = {
          id: auctionDoc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Auction;

        if (!isAdmin && auctionData.createdBy !== userProfile?.uid) {
          toast.error('You do not have permission to edit this auction');
          navigate('/auctioneer/dashboard');
          return;
        }

        setAuction(auctionData);
      } else {
        toast.error('Auction not found');
        navigate('/auctioneer/dashboard');
      }
    } catch (error) {
      console.error('Error loading auction:', error);
      toast.error('Failed to load auction');
    } finally {
      setLoading(false);
    }
  };

  const parseFileName = (filename: string): { sku: string; imageNumber: string } | null => {
    // Expected format: SKU_01.jpg or SKU_02.png
    const match = filename.match(/^(.+?)_(\d{2})\.[^.]+$/);
    
    if (match) {
      return {
        sku: match[1],
        imageNumber: match[2]
      };
    }
    
    return null;
  };

  const handleFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const parsedImages: UploadedImage[] = [];
    const skipped: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const parsed = parseFileName(file.name);

      if (parsed) {
        parsedImages.push({
          file,
          sku: parsed.sku,
          imageNumber: parsed.imageNumber,
          status: 'pending'
        });
      } else {
        skipped.push(file.name);
      }
    }

    setImages(parsedImages);

    if (skipped.length > 0) {
      toast.error(`Skipped ${skipped.length} files with incorrect naming format`);
      console.log('Skipped files:', skipped);
    }

    toast.success(`Loaded ${parsedImages.length} images ready for upload`);
  };

  const uploadAllImages = async () => {
    if (!auction || images.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: images.length });

    const updatedImages = [...images];
    const lotImageMap = new Map<string, string[]>();

    // Initialize map with existing images
    auction.lots.forEach(lot => {
      lotImageMap.set(lot.sku, [...(lot.images || [])]);
    });

    for (let i = 0; i < updatedImages.length; i++) {
      const image = updatedImages[i];
      
      try {
        image.status = 'uploading';
        setImages([...updatedImages]);

        // Upload to Firebase Storage
        const filename = `${image.sku}_${image.imageNumber}.${image.file.name.split('.').pop()}`;
        const storageRef = ref(storage, `auction-images/${filename}`);
        
        await uploadBytes(storageRef, image.file);
        const url = await getDownloadURL(storageRef);

        image.url = url;
        image.status = 'success';

        // Add URL to lot's images
        const currentImages = lotImageMap.get(image.sku) || [];
        currentImages.push(url);
        lotImageMap.set(image.sku, currentImages);

        setUploadProgress({ current: i + 1, total: images.length });
        setImages([...updatedImages]);
      } catch (error: any) {
        console.error(`Error uploading ${image.file.name}:`, error);
        image.status = 'error';
        image.error = error.message;
        setImages([...updatedImages]);
      }
    }

    // Update auction lots with new image URLs
    try {
      const updatedLots = auction.lots.map(lot => ({
        ...lot,
        images: lotImageMap.get(lot.sku) || lot.images || []
      }));

      await updateDoc(doc(db, 'auctions', auction.id), {
        lots: updatedLots,
        updatedAt: new Date()
      });

      const successCount = updatedImages.filter(img => img.status === 'success').length;
      const errorCount = updatedImages.filter(img => img.status === 'error').length;

      toast.success(`Upload complete! ${successCount} images uploaded successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
    } catch (error) {
      console.error('Error updating auction:', error);
      toast.error('Failed to update auction with new images');
    } finally {
      setUploading(false);
    }
  };

  const clearImages = () => {
    setImages([]);
    setUploadProgress({ current: 0, total: 0 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="card text-center py-12">
        <p className="text-xl text-gray-600">Auction not found</p>
      </div>
    );
  }

  // Group images by SKU
  const imagesBySku = images.reduce((acc, img) => {
    if (!acc[img.sku]) acc[img.sku] = [];
    acc[img.sku].push(img);
    return acc;
  }, {} as Record<string, UploadedImage[]>);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Bulk Image Upload</h1>
          <p className="text-gray-600 mt-1">{auction.title}</p>
        </div>
        <button
          onClick={() => navigate(`/auctioneer/edit-auction/${auctionId}`)}
          className="btn-secondary"
        >
          Back to Edit
        </button>
      </div>

      {/* Instructions */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="font-bold text-lg mb-2">📋 Image Naming Instructions</h3>
        <div className="text-sm space-y-1">
          <p>• Images must be named: <code className="bg-white px-2 py-1 rounded">SKU_01.jpg</code>, <code className="bg-white px-2 py-1 rounded">SKU_02.jpg</code>, etc.</p>
          <p>• Example: If SKU is "115252", name images: <code className="bg-white px-2 py-1 rounded">115252_01.jpg</code>, <code className="bg-white px-2 py-1 rounded">115252_02.jpg</code></p>
          <p>• The number should be 2 digits (01, 02, 03... 10, 11, etc.)</p>
          <p>• Supported formats: .jpg, .jpeg, .png, .gif, .webp</p>
          <p>• Select all images from your folder at once</p>
        </div>
      </div>

      {/* File Input */}
      <div className="card">
        <h3 className="text-xl font-bold mb-4">Select Images</h3>
        
        <div className="mb-4">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFolderSelect}
            disabled={uploading}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <p className="text-sm text-gray-600 mt-2">
            Select multiple images at once (Ctrl+A or Cmd+A to select all in a folder)
          </p>
        </div>

        {images.length > 0 && (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-semibold">
                {images.length} images loaded ({Object.keys(imagesBySku).length} SKUs)
              </p>
              {uploadProgress.total > 0 && (
                <p className="text-sm text-gray-600">
                  Progress: {uploadProgress.current} / {uploadProgress.total}
                </p>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={clearImages}
                disabled={uploading}
                className="btn-secondary"
              >
                Clear
              </button>
              <button
                onClick={uploadAllImages}
                disabled={uploading || images.length === 0}
                className="btn-primary disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload All Images'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Preview by SKU */}
      {images.length > 0 && (
        <div className="card">
          <h3 className="text-xl font-bold mb-4">Preview by SKU</h3>
          
          <div className="space-y-6">
            {Object.entries(imagesBySku).map(([sku, skuImages]) => {
              const lot = auction.lots.find(l => l.sku === sku);
              const sortedImages = skuImages.sort((a, b) => 
                a.imageNumber.localeCompare(b.imageNumber)
              );

              return (
                <div key={sku} className="border rounded-lg p-4">
                  <div className="mb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-bold">SKU: {sku}</h4>
                        {lot ? (
                          <p className="text-sm text-gray-600">{lot.title}</p>
                        ) : (
                          <p className="text-sm text-red-600">⚠️ SKU not found in auction</p>
                        )}
                      </div>
                      <span className="text-sm text-gray-600">
                        {skuImages.length} image(s)
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-6 gap-2">
                    {sortedImages.map((image, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={URL.createObjectURL(image.file)}
                          alt={`${sku}_${image.imageNumber}`}
                          className="w-full h-24 object-cover rounded"
                        />
                        <div className="absolute top-1 right-1">
                          {image.status === 'pending' && (
                            <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded">
                              Pending
                            </span>
                          )}
                          {image.status === 'uploading' && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">
                              Uploading...
                            </span>
                          )}
                          {image.status === 'success' && (
                            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">
                              ✓
                            </span>
                          )}
                          {image.status === 'error' && (
                            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">
                              ✗
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-center mt-1 text-gray-600">
                          _{image.imageNumber}
                        </p>
                      </div>
                    ))}
                  </div>

                  {skuImages.some(img => img.status === 'error') && (
                    <div className="mt-2 text-sm text-red-600">
                      {skuImages.filter(img => img.status === 'error').map(img => (
                        <p key={img.file.name}>
                          • {img.file.name}: {img.error}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkImageUpload;